import { ReactNode, useCallback, useMemo } from 'react';

import { PendingAction, loaders } from 'entities';

import { useUser } from 'hooks/user';

import { useProject } from '../Project';

import { sumUp } from 'utils/array';

import { PendingActionsContext } from './pendingActions.context';
import { buildPendingEntries, PendingEntries } from './utils';

const THREE_MINUTES = 3;

export function PendingActionsCountProvider({ children }: { children: ReactNode }) {
  const user = useUser();

  const { projectList } = useProject();

  const {
    data: asyncCounts,
    overwrite,
    loading,
    retry,
  } = loaders.general.pendingActionsCounts.use({
    defaultValue: { total: 0, byType: {} },
    blockLoad: !user.id,
    reloadOnChangeRef: user?.team?.id,
    refreshMinutes: THREE_MINUTES,
  });

  const pendingEntries = useMemo(() => {
    if (!user.id) return {} as PendingEntries;

    return buildPendingEntries({
      asyncCounts,
      projects: projectList,
      user,
    });
  }, [asyncCounts, projectList, user]);

  const pendingActionsNumber = useMemo(() => {
    if (!user.id) return 0;

    return sumUp(Object.values(pendingEntries), 'count');
  }, [user.id, pendingEntries]);

  const decreaseCount = useCallback(
    (action: PendingAction) => {
      overwrite((old) => ({
        total: (old.total || 1) - 1,

        byType: {
          ...old.byType,

          [action]: (old.byType[action] || 1) - 1,
        },
      }));
    },
    [overwrite],
  );

  const contextData = useMemo(
    () => ({
      total: pendingActionsNumber,
      pendingEntries,
      decreaseCount,
      loading: pendingActionsNumber ? false : loading,
      refetchActions: retry,
    }),
    [pendingActionsNumber, pendingEntries, decreaseCount, loading, retry],
  );

  return (
    <PendingActionsContext.Provider value={contextData}>{children}</PendingActionsContext.Provider>
  );
}
